// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { Artifact, ProcedureDetail } from '../types/procedures';

interface UpstreamStep {
  seq?: number;
  instruction?: string;
  check_type?: string;
  is_gate?: boolean;
  citations?: string[];
}

interface UpstreamAffectedEquipment {
  role?: string;
  type?: string;
  asset_binding?: string;
}

/**
 * Maps a real `Artifact` envelope to the procedure view/create screens'
 * normalized shape. Fields with no upstream equivalent yet (prerequisites,
 * contingencies, closeout, most of governance — see `ProcedureDetail`'s
 * docstring) always come back empty/false rather than being fabricated.
 */
export function toProcedureDetail(artifact: Artifact): ProcedureDetail {
  const payload = artifact.payload ?? {};
  const categories = artifact.categories ?? {};
  const governance = (payload.governance ?? {}) as Record<string, unknown>;

  const affectedEquipment = ((payload.affected_equipment as UpstreamAffectedEquipment[]) ?? []).map(
    item => ({
      role: item.role ?? '',
      type: item.type ?? '',
      asset: item.asset_binding ?? '',
    })
  );

  const procedure = ((payload.steps as UpstreamStep[]) ?? []).map(s => ({
    step: s.seq ?? 0,
    action: s.instruction ?? '',
    check: s.check_type ?? '',
    passCriteria: '',
    gate: Boolean(s.is_gate),
    initials: '',
    source: s.citations?.[0] ?? artifact.artifact_id ?? '',
  }));

  return {
    artifactId: artifact.artifact_id ?? '',
    artifactType: artifact.artifact_type ?? '',
    title: artifact.title ?? '',
    procedureNumber: artifact.artifact_id ?? '',
    version: artifact.version ?? '',
    status: artifact.status ?? '',
    classification: categories.domain ?? '',
    equipmentType: (payload.equipment_type as string) ?? categories.equipment_type ?? '',
    description: (payload.description_of_work as string) ?? '',
    affectedEquipment,
    prerequisites: { qualifications: '', approvals: '', precondition: '' },
    procedure,
    contingencies: [],
    closeout: { turnoverFrom: '', turnoverTo: '', signoffRole: '' },
    governance: {
      mopRequired: Boolean(governance.mop_required),
      changeApproval: Boolean(governance.change_approval),
      loto: Boolean(governance.loto),
      redundancy: (governance.redundancy as string) ?? '',
      escalatesToEop: (governance.escalates_to_eop as string) ?? '',
    },
  };
}
